import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('component.region.selector');
import { EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, firstValueFrom } from 'rxjs';
import { Service } from "src/libs/portal/season/service";
import {
    RegionNode,
    RegionDataSource,
    RegionSelection,
    RegionSelectionBlock,
    RegionSelectionService,
    RegionTreeResponse,
} from 'src/app/region-selection.service';

interface RegionUiPosition {
    region_id: string;
    region_name: string;
    map_x_percent: number;
    map_y_percent: number;
    label_anchor: 'LEFT' | 'RIGHT' | 'TOP' | 'BOTTOM';
    label_offset_x: number;
    label_offset_y: number;
    z_index: number;
    mobile_display_order: number;
}

@Component({
    selector: 'wiz-component-region-selector',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/component.region.selector/view.scss */
:host {
  display: block;
  min-width: 0;
}`],
})
export class ComponentRegionSelectorComponent implements OnInit, OnDestroy {
    @Input() public mode: 'map' | 'compact' = 'map';
    @Input() public context: 'dashboard' | 'policy-simulator' = 'dashboard';
    @Input() public summary: any = null;
    @Output() public regionSelected = new EventEmitter<RegionSelection>();

    public tree: RegionTreeResponse | null = null;
    public selected: RegionSelection | null = null;
    public positions: RegionUiPosition[] = [];
    public modalOpen: boolean = false;
    public activeMetropolitanId: string = '';
    public dataSource: RegionDataSource | null = null;
    public blockedAttempt: RegionSelectionBlock | null = null;
    public mapImageAvailable: boolean | null = null;

    private readonly subscriptions = new Subscription();
    private returnFocusElement: HTMLElement | null = null;

    constructor(@Inject( Service)         public service: Service,@Inject( RegionSelectionService)         public regionSelection: RegionSelectionService,@Inject( HttpClient)         private http: HttpClient,    ) { }

    public async ngOnInit() {
        await this.service.init();
        this.subscriptions.add(this.regionSelection.tree$.subscribe(tree => {
            this.tree = tree;
            if (!this.activeMetropolitanId) this.syncActiveMetropolitan();
            void this.service.render();
        }));
        this.subscriptions.add(this.regionSelection.selection$.subscribe(selection => {
            this.selected = selection;
            this.syncActiveMetropolitan();
            void this.service.render();
        }));
        this.subscriptions.add(this.regionSelection.dataSource$.subscribe(source => {
            this.dataSource = source;
            void this.service.render();
        }));
        this.subscriptions.add(this.regionSelection.blocked$.subscribe(blocked => {
            this.blockedAttempt = blocked;
            void this.service.render();
        }));
        this.loadMapImageAvailability();
        await Promise.all([this.regionSelection.loadTree(), this.loadPositions()]);
        await this.service.render();
    }

    public ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.restorePageScroll();
    }

    @HostListener('document:keydown.escape')
    public onEscape() {
        if (this.modalOpen) void this.closeSelector();
    }

    public get metropolitanRegions(): RegionNode[] {
        const order = new Map(this.positions.map(item => [item.region_id, item.mobile_display_order]));
        return [...(this.tree?.regions || [])].sort((left, right) =>
            (order.get(left.region_id) || left.display_order) - (order.get(right.region_id) || right.display_order)
        );
    }

    public get activeMetropolitan(): RegionNode | null {
        return this.metropolitanRegions.find(region => region.region_id === this.activeMetropolitanId) || null;
    }

    public get activeChildren(): RegionNode[] {
        return this.activeMetropolitan?.children || [];
    }

    public async openSelector(regionId: string = '', event?: Event) {
        this.returnFocusElement = event?.currentTarget as HTMLElement || document.activeElement as HTMLElement;
        if (regionId) this.activeMetropolitanId = regionId;
        else this.syncActiveMetropolitan();
        if (!this.activeMetropolitanId) this.activeMetropolitanId = this.metropolitanRegions[0]?.region_id || '';
        this.regionSelection.clearBlocked();
        this.modalOpen = true;
        document.body.classList.add('region-selector-open');
        await this.service.render();
        window.setTimeout(() => document.getElementById('region-selector-close')?.focus(), 0);
    }

    public async activateMapRegion(region: RegionNode, event?: Event) {
        await this.openSelector(region.region_id, event);
    }

    public async closeSelector() {
        this.modalOpen = false;
        this.restorePageScroll();
        await this.service.render();
        window.setTimeout(() => this.returnFocusElement?.focus(), 0);
    }

    public async closeFromBackdrop(event: Event) {
        if (event.target === event.currentTarget) await this.closeSelector();
    }

    public async chooseMetropolitan(region: RegionNode) {
        this.activeMetropolitanId = region.region_id;
        this.regionSelection.clearBlocked();
        await this.service.render();
    }

    public async selectNode(node: RegionNode) {
        const selection = this.regionSelection.select(node);
        if (!selection) {
            await this.service.render();
            return;
        }
        this.regionSelected.emit(selection);
        await this.closeSelector();
    }

    public async selectBlockedParent() {
        const parent = this.activeMetropolitan;
        if (!parent || parent.analysis_status !== 'READY') return;
        await this.selectNode(parent);
    }

    public positionStyle(region: RegionNode): Record<string, string> {
        const position = this.positions.find(item => item.region_id === region.region_id);
        return position
            ? {
                left: `${position.map_x_percent}%`,
                top: `${position.map_y_percent}%`,
                '--pin-z-index': `${position.z_index || 2}`,
            }
            : { display: 'none' };
    }

    public labelStyle(region: RegionNode): Record<string, string> {
        const position = this.positions.find(item => item.region_id === region.region_id);
        return position ? {
            '--label-offset-x': `${position.label_offset_x}px`,
            '--label-offset-y': `${position.label_offset_y}px`,
        } : {};
    }

    public labelClass(region: RegionNode): string {
        const position = this.positions.find(item => item.region_id === region.region_id);
        return `region-pin__label region-pin__label--${(position?.label_anchor || 'RIGHT').toLowerCase()}`;
    }

    public pinClass(region: RegionNode): string {
        const selected = this.selected?.region_id === region.region_id
            || this.selected?.parent_region_id === region.region_id;
        const status = region.analysis_status.toLowerCase();
        return `region-pin region-pin--${status}${selected ? ' is-selected' : ''}`;
    }

    public itemClass(region: RegionNode): string {
        const selected = this.selected?.region_id === region.region_id;
        return `region-choice region-choice--${region.analysis_status.toLowerCase()}${selected ? ' is-selected' : ''}`;
    }

    public statusLabel(status: string): string {
        return this.regionSelection.statusLabel(status);
    }

    public statusIcon(status: string): string {
        const icons: Record<string, string> = {
            READY: '✓',
            PENDING: '…',
            UNAVAILABLE: '—',
        };
        return icons[status] || '!';
    }

    public blockedMessage(): string {
        if (!this.blockedAttempt) return '';
        if (this.context !== 'policy-simulator') return this.blockedAttempt.message;
        return this.blockedAttempt.analysis_status === 'PENDING'
            ? '해당 기초자치단체의 상세 LQ 및 정책시뮬레이션 데이터는 현재 검증·확장 중입니다. 상위 광역자치단체 단위 분석을 이용할 수 있습니다.'
            : '해당 지역은 현재 정책시뮬레이션에 필요한 분석 데이터가 제공되지 않습니다.';
    }

    public dataSourceLabel(): string {
        return this.regionSelection.dataSourceLabel(this.dataSource);
    }

    public isSelected(region: RegionNode): boolean {
        return this.selected?.region_id === region.region_id;
    }

    public formatCount(value: number): string {
        if (value === null || value === undefined) return '미확보';
        return Number(value).toLocaleString('ko-KR');
    }

    private async loadPositions() {
        const payload: any = await firstValueFrom(
            this.http.get('/assets/data/region-ui-position.json')
        );
        this.positions = Array.isArray(payload?.positions) ? payload.positions : [];
    }

    private loadMapImageAvailability() {
        const image = new Image();
        image.onload = () => {
            this.mapImageAvailable = true;
            void this.service.render();
        };
        image.onerror = () => {
            this.mapImageAvailable = false;
            void this.service.render();
        };
        image.src = '/assets/brand/3d-map-with-borders-regions_97886-11245.jpg';
    }

    private syncActiveMetropolitan() {
        if (!this.selected) return;
        this.activeMetropolitanId = this.selected.region_type === 'METROPOLITAN'
            ? this.selected.region_id
            : this.selected.parent_region_id || '';
    }

    private restorePageScroll() {
        document.body.classList.remove('region-selector-open');
    }
}

export default ComponentRegionSelectorComponent;